import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { haptic } from "@/lib/haptics";
import { compressImageFile, dataUrlSizeKB } from "@/lib/image";
import { reverseGeocode, parseFrenchAddress } from "@/lib/geocode";
import { ScanLine, Hash, MapPin, FileEdit, ChevronLeft, ChevronRight, Loader2, Search, User, FileUp } from "lucide-react";

/**
 * AddProspectSheet — bottom sheet with 4 paths to create a lead:
 *   1. Scanner une carte de visite (Claude vision OCR)
 *   2. Rechercher par SIRET/SIREN (recherche-entreprises.api.gouv.fr)
 *   3. Géolocalisation (capture GPS, prefill coords)
 *   4. Saisie manuelle
 *
 * Usage:
 *   const [open, setOpen] = useState(false);
 *   <AddProspectSheet open={open} onOpenChange={setOpen} onCreated={(p) => ...} />
 */
export default function AddProspectSheet({ open, onOpenChange, onCreated }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState("menu"); // menu | scan | siret | geo | manual
  const [loading, setLoading] = useState(false);

  // SIRET mode
  const [siret, setSiret] = useState("");
  const [siretData, setSiretData] = useState(null);
  const [siretSearchMode, setSiretSearchMode] = useState("name"); // "name" | "siret"
  const [nameQuery, setNameQuery] = useState("");
  const [nameResults, setNameResults] = useState(null);

  // Scan mode
  const [scanPreview, setScanPreview] = useState(null);
  const [scanData, setScanData] = useState(null);
  const fileInputRef = useRef(null);

  // Manual / geo prefill
  const [form, setForm] = useState({
    name: "", company: "", title: "", email: "", phone: "", address: "", postal_code: "", city: "", siret: "",
    gps_lat: null, gps_lng: null, source: "Manuel",
  });

  const reset = () => {
    setMode("menu");
    setSiret(""); setSiretData(null);
    setNameQuery(""); setNameResults(null); setSiretSearchMode("name");
    setScanPreview(null); setScanData(null);
    setForm({ name: "", company: "", title: "", email: "", phone: "", address: "", postal_code: "", city: "", siret: "", gps_lat: null, gps_lng: null, source: "Manuel" });
  };

  const close = () => { reset(); onOpenChange(false); };

  // ──────────────── SIRET ────────────────
  const lookupSiret = async () => {
    const clean = siret.replace(/\s+/g, "");
    if (!/^\d{9}(\d{5})?$/.test(clean)) {
      toast.error("SIRET (14) ou SIREN (9) — chiffres uniquement");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get(`/siret/${clean}`);
      setSiretData(data);
      setForm((f) => ({
        ...f,
        company: data.company_name || "",
        address: data.address || "",
        postal_code: data.postal_code || "",
        city: data.city || "",
        siret: data.siret || clean,
        source: "SIRET",
      }));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      toast.error(
        detail ||
          (status === 404
            ? "Entreprise introuvable"
            : !navigator.onLine
              ? "Hors-ligne — connectez-vous à Internet"
              : "Service SIRET momentanément indisponible")
      );
    } finally {
      setLoading(false);
    }
  };

  const searchByName = async () => {
    const q = nameQuery.trim();
    if (q.length < 2) {
      toast.error("Tapez au moins 2 caractères");
      return;
    }
    setLoading(true);
    setNameResults(null);
    try {
      const { data } = await api.get(`/siret/search?q=${encodeURIComponent(q)}&limit=10`);
      setNameResults(data);
      if (data.length === 0) toast.info("Aucune entreprise trouvée");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(
        detail ||
          (!navigator.onLine
            ? "Hors-ligne — connectez-vous à Internet"
            : "Service recherche momentanément indisponible")
      );
    } finally {
      setLoading(false);
    }
  };

  const selectCompany = (c) => {
    setSiretData(c);
    setForm((f) => ({
      ...f,
      company: c.company_name || "",
      address: c.address || "",
      postal_code: c.postal_code || "",
      city: c.city || "",
      siret: c.siret || "",
      source: "SIRET",
    }));
    setNameResults(null);
  };

  // ──────────────── Scan carte ────────────────
  const handleCardFile = async (file) => {
    if (!file) return;
    setLoading(true);
    try {
      // Compress client-side (iPhone camera photos are huge → hit ingress 1MB limit)
      const dataUrl = await compressImageFile(file, { maxSide: 1600, quality: 0.75 });
      setScanPreview(dataUrl);
      // Safety net — if still huge, recompress harder
      let finalUrl = dataUrl;
      if (dataUrlSizeKB(finalUrl) > 900) {
        finalUrl = await compressImageFile(file, { maxSide: 1200, quality: 0.6 });
      }
      const { data } = await api.post("/ai/scan-card", { image_base64: finalUrl });
      setScanData(data);
      const parsed = parseFrenchAddress(data.address || "");
      setForm((f) => ({
        ...f,
        name: data.name || "",
        company: data.company || "",
        title: data.title || "",
        email: data.email || "",
        phone: data.phone || "",
        address: parsed.street || data.address || "",
        postal_code: parsed.postal_code || "",
        city: parsed.city || "",
        source: "Carte scannée",
      }));
      haptic("success");
      toast.success("Carte analysée ✨");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      const msg = detail
        ? String(detail).slice(0, 120)
        : status === 413
          ? "Image trop lourde — réessayez avec une photo plus nette ou moins zoomée"
          : !navigator.onLine
            ? "Hors-ligne — connectez-vous à Internet pour analyser la carte"
            : "Échec de l'analyse — vérifiez la photo et réessayez";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // ──────────────── Géoloc ────────────────
  const captureGeo = () => {
    if (!navigator.geolocation) {
      toast.error("Géolocalisation non disponible");
      return;
    }
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        // Reverse-geocode to auto-detect street / postal code / city
        let detected = null;
        try {
          detected = await reverseGeocode(lat, lng);
        } catch { /* ignore */ }
        setForm((f) => ({
          ...f,
          gps_lat: lat,
          gps_lng: lng,
          address: detected?.address || f.address,
          postal_code: detected?.postal_code || f.postal_code,
          city: detected?.city || f.city,
          source: "Road Maps",
        }));
        setLoading(false);
        haptic("success");
        if (detected?.city) {
          toast.success(`📍 ${detected.city}${detected.postal_code ? ` (${detected.postal_code})` : ""}`);
        } else {
          toast.success(`Position captée : ${lat.toFixed(4)}, ${lng.toFixed(4)}`);
        }
        setMode("manual");
      },
      (err) => {
        setLoading(false);
        toast.error(err.code === 1 ? "Autorisez la géolocalisation" : "Position introuvable");
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // ──────────────── Create ────────────────
  const createProspect = async () => {
    if (!form.name && !form.company) {
      toast.error("Ajoutez au moins un nom ou une entreprise");
      return;
    }
    setLoading(true);
    try {
      const { data: created } = await api.post("/prospects", {
        ...form,
        status: "À contacter",
      });
      toast.success("Lead ajouté ✨");
      haptic("success");
      onCreated?.(created);
      close();
      // Open visit-report flow only for "Road Maps" or "Carte scannée" sources
      // (i.e. the user just met the lead in person and should record the visit)
      if (created?.prospect_id && (form.source === "Road Maps" || form.source === "Carte scannée")) {
        navigate(`/visit-report/${created.prospect_id}`);
      }
    } catch (e) {
      toast.error("Erreur à la création");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <SheetContent
        side="bottom"
        className="rounded-t-3xl p-0 border-0 h-[92vh] max-h-[92vh] overflow-hidden mx-auto sm:max-w-md flex flex-col"
        data-testid="add-prospect-sheet"
      >
        {/* grabber */}
        <div className="pt-3 pb-1 flex justify-center shrink-0">
          <div className="w-10 h-1 rounded-full bg-slate-300" />
        </div>

        {mode === "menu" && (
          <div className="px-5 pb-8 flex-1 overflow-y-auto">
            <h2 className="font-display text-xl font-bold text-[var(--text)] text-center mt-2">
              Ajouter un lead
            </h2>
            <p className="text-sm text-zinc-500 text-center mt-1">
              Choisissez votre méthode
            </p>
            <div className="mt-6 grid grid-cols-2 gap-3">
              <ModeCard icon={ScanLine} label="Scanner une carte" grad="from-emerald-500 to-orange-500" onClick={() => { setMode("scan"); setTimeout(() => fileInputRef.current?.click(), 50); }} testId="mode-scan" />
              <ModeCard icon={Hash} label="Rechercher entreprise" grad="from-sky-500 to-indigo-500" onClick={() => setMode("siret")} testId="mode-siret" />
              <ModeCard icon={MapPin} label="Ma position GPS" grad="from-emerald-500 to-teal-500" onClick={captureGeo} testId="mode-geo" loading={loading} />
              <ModeCard icon={FileEdit} label="Saisie manuelle" grad="from-amber-500 to-orange-500" onClick={() => setMode("manual")} testId="mode-manual" />
              <div className="col-span-2">
                <button
                  type="button"
                  onClick={() => { onOpenChange(false); navigate("/import"); }}
                  data-testid="mode-import"
                  className="w-full flex items-center gap-3 bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-3.5 text-left active:scale-[0.98] transition-transform"
                >
                  <div className="w-11 h-11 rounded-xl bg-white/20 grid place-items-center shrink-0">
                    <FileUp className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-white">Importer un fichier</div>
                    <div className="text-[11px] text-white/70 truncate">CSV / Excel · l'IA qualifie automatiquement</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-white/70 shrink-0" />
                </button>
              </div>
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => handleCardFile(e.target.files?.[0])} />
          </div>
        )}

        {mode === "scan" && (
          <div className="px-5 pb-8 flex-1 overflow-y-auto">
            <BackBar onBack={() => { setScanPreview(null); setScanData(null); setMode("menu"); }} title="Scanner une carte" />
            {!scanPreview ? (
              <div className="mt-4 text-center py-10">
                <div className="w-20 h-20 rounded-3xl bg-emerald-500/15 grid place-items-center mx-auto">
                  <ScanLine className="w-8 h-8 text-emerald-400" />
                </div>
                <p className="text-sm text-zinc-400 mt-4">Prenez une photo de la carte de visite</p>
                <Button onClick={() => fileInputRef.current?.click()} className="mt-4 rounded-full bg-emerald-500/100 hover:bg-emerald-500 h-11 px-6" data-testid="scan-open-camera">
                  Ouvrir l'appareil photo
                </Button>
                <input ref={fileInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => handleCardFile(e.target.files?.[0])} />
              </div>
            ) : loading ? (
              <div className="mt-6 text-center">
                <img src={scanPreview} alt="carte" className="w-full max-h-60 object-contain rounded-2xl" />
                <div className="flex items-center justify-center gap-2 mt-4 text-emerald-400">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm font-semibold">Analyse en cours…</span>
                </div>
              </div>
            ) : scanData ? (
              <div className="mt-4">
                <img src={scanPreview} alt="carte" className="w-full max-h-40 object-contain rounded-2xl mb-4" />
                <ManualForm form={form} setForm={setForm} compact />
                <Button onClick={createProspect} disabled={loading} className="w-full mt-4 rounded-full bg-emerald-500/100 hover:bg-emerald-500 h-12 font-semibold" data-testid="scan-confirm-create">
                  {loading ? "Enregistrement…" : "Enregistrer le lead"}
                </Button>
              </div>
            ) : null}
          </div>
        )}

        {mode === "siret" && (
          <div className="px-5 pb-8 flex-1 overflow-y-auto">
            <BackBar onBack={() => { setSiretData(null); setSiret(""); setNameResults(null); setNameQuery(""); setMode("menu"); }} title="Rechercher une entreprise" />

            {/* Tab switcher */}
            <div className="mt-4 flex gap-1 bg-[var(--surface-alt)] rounded-full p-1">
              <button
                onClick={() => { setSiretSearchMode("name"); setSiretData(null); }}
                data-testid="tab-by-name"
                className={`flex-1 py-1.5 rounded-full text-xs font-semibold transition-colors ${siretSearchMode === "name" ? "bg-[var(--surface)] text-sky-300 border border-sky-500/40" : "text-zinc-400"}`}
              >
                Par nom
              </button>
              <button
                onClick={() => { setSiretSearchMode("siret"); setNameResults(null); setSiretData(null); }}
                data-testid="tab-by-siret"
                className={`flex-1 py-1.5 rounded-full text-xs font-semibold transition-colors ${siretSearchMode === "siret" ? "bg-[var(--surface)] text-sky-300 border border-sky-500/40" : "text-zinc-400"}`}
              >
                Par SIRET
              </button>
            </div>

            {siretSearchMode === "name" ? (
              <div className="mt-4">
                <Label className="text-xs font-semibold">Nom, marque ou ville</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={nameQuery}
                    onChange={(e) => setNameQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && searchByName()}
                    placeholder="Ex: Carrefour, Decathlon Paris…"
                    className="rounded-2xl flex-1 h-11"
                    data-testid="name-search-input"
                  />
                  <Button onClick={searchByName} disabled={loading || !nameQuery} className="rounded-full bg-sky-500/100 hover:bg-sky-600 h-11 px-5" data-testid="name-search-btn">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  </Button>
                </div>

                {nameResults && nameResults.length > 0 && !siretData && (
                  <div className="mt-4 bg-[var(--surface)] rounded-xl shadow-soft overflow-hidden divide-y divide-[var(--border-soft)] max-h-80 overflow-y-auto">
                    {nameResults.map((c, i) => (
                      <button
                        key={`${c.siret}-${i}`}
                        onClick={() => selectCompany(c)}
                        data-testid={`result-${i}`}
                        className="w-full flex items-start gap-3 px-4 py-3 text-left active:bg-[var(--surface-alt)] transition-colors"
                      >
                        <div className="w-9 h-9 rounded-xl bg-sky-500/15 text-sky-300 grid place-items-center shrink-0">
                          <Hash className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-[14px] text-[var(--text)] truncate">{c.company_name}</div>
                          <div className="text-xs text-zinc-500 truncate">{c.city}{c.postal_code ? ` · ${c.postal_code}` : ""}</div>
                          <div className="text-[11px] text-zinc-500 mt-0.5">SIRET {c.siret}</div>
                        </div>
                        <ChevronLeft className="w-4 h-4 text-slate-300 rotate-180 mt-2 shrink-0" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="mt-4">
                <Label className="text-xs font-semibold">SIRET (14) ou SIREN (9)</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={siret}
                    onChange={(e) => setSiret(e.target.value)}
                    placeholder="Ex: 55203781900017"
                    className="rounded-2xl flex-1 h-11"
                    inputMode="numeric"
                    data-testid="siret-input"
                  />
                  <Button onClick={lookupSiret} disabled={loading || !siret} className="rounded-full bg-sky-500/100 hover:bg-sky-600 h-11 px-5" data-testid="siret-lookup-btn">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            )}

            {siretData && (
              <div className="mt-5">
                <div className="bg-gradient-to-br from-sky-50 to-indigo-50 rounded-2xl p-4 border border-sky-100">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-sky-300">INPI · validé</div>
                  <div className="font-display text-lg font-bold text-[var(--text)] mt-1">{siretData.company_name}</div>
                  <div className="text-xs text-zinc-400 mt-1">{siretData.address}</div>
                  {siretData.naf_label && <div className="text-[11px] text-zinc-500 mt-1">NAF : {siretData.naf_label}</div>}
                  <div className="text-[11px] text-zinc-500 mt-0.5">SIRET : {siretData.siret}</div>
                </div>
                <div className="text-[11px] text-zinc-500 mt-3 mb-1 px-1">Complétez le contact, les autres champs sont remplis automatiquement.</div>
                <ManualForm form={form} setForm={setForm} compact />
                <Button onClick={createProspect} disabled={loading} className="w-full mt-4 rounded-full bg-emerald-500/100 hover:bg-emerald-500 h-12 font-semibold" data-testid="siret-confirm-create">
                  {loading ? "Enregistrement…" : "Enregistrer le lead"}
                </Button>
              </div>
            )}
          </div>
        )}

        {mode === "manual" && (
          <div className="px-5 pb-8 flex-1 overflow-y-auto">
            <BackBar onBack={() => setMode("menu")} title="Saisie manuelle" />
            {form.gps_lat != null && (
              <div className="mt-3 flex items-center gap-2 bg-emerald-500/10 rounded-2xl px-3 py-2 text-xs text-emerald-300 font-semibold">
                <MapPin className="w-3.5 h-3.5" />
                Position GPS : {form.gps_lat.toFixed(4)}, {form.gps_lng.toFixed(4)}
              </div>
            )}
            <div className="mt-3">
              <ManualForm form={form} setForm={setForm} />
            </div>
            <Button onClick={createProspect} disabled={loading} className="w-full mt-5 rounded-full bg-emerald-500/100 hover:bg-emerald-500 h-12 font-semibold" data-testid="manual-confirm-create">
              {loading ? "Enregistrement…" : "Enregistrer le lead"}
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function ModeCard({ icon: Icon, label, grad, onClick, testId, loading }) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      data-testid={testId}
      className={`relative aspect-square rounded-3xl p-4 text-white overflow-hidden bg-gradient-to-br ${grad} shadow-soft active:scale-95 transition-transform disabled:opacity-70 flex flex-col justify-between`}
    >
      <div className="absolute -right-4 -bottom-4 w-24 h-24 rounded-full bg-white/10 blur-xl" />
      <div className="relative">
        <div className="w-11 h-11 rounded-2xl bg-white/20 backdrop-blur-sm grid place-items-center">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Icon className="w-5 h-5" strokeWidth={2.2} />}
        </div>
      </div>
      <div className="relative font-display font-bold text-sm leading-tight text-left">
        {label}
      </div>
    </button>
  );
}

function BackBar({ onBack, title }) {
  return (
    <div className="flex items-center gap-2 mt-2">
      <button onClick={onBack} className="w-9 h-9 rounded-full grid place-items-center active:bg-[var(--surface-alt)]" data-testid="back-to-menu">
        <ChevronLeft className="w-5 h-5 text-zinc-300" />
      </button>
      <h2 className="font-display text-lg font-bold text-[var(--text)]">{title}</h2>
    </div>
  );
}

function ManualForm({ form, setForm, compact }) {
  const up = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const autoDetectFromAddress = () => {
    const parsed = parseFrenchAddress(form.address || "");
    if (!parsed.postal_code && !parsed.city) {
      toast.error("Aucun code postal détecté dans l'adresse");
      return;
    }
    setForm((f) => ({
      ...f,
      address: parsed.street || f.address,
      postal_code: parsed.postal_code || f.postal_code,
      city: parsed.city || f.city,
    }));
    toast.success(`📍 ${parsed.city || ""}${parsed.postal_code ? ` (${parsed.postal_code})` : ""}`);
  };
  return (
    <div className={compact ? "space-y-2" : "space-y-3"}>
      <div>
        <Label className="text-xs font-semibold">Nom du contact</Label>
        <Input value={form.name} onChange={(e) => up("name", e.target.value)} placeholder="Jean Dupont" className="rounded-2xl mt-1 h-11" data-testid="form-name" />
      </div>
      <div>
        <Label className="text-xs font-semibold">Entreprise</Label>
        <Input value={form.company} onChange={(e) => up("company", e.target.value)} placeholder="Acme SAS" className="rounded-2xl mt-1 h-11" data-testid="form-company" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs font-semibold">Fonction</Label>
          <Input value={form.title} onChange={(e) => up("title", e.target.value)} placeholder="Directeur" className="rounded-2xl mt-1 h-11" data-testid="form-title" />
        </div>
        <div>
          <Label className="text-xs font-semibold">Téléphone</Label>
          <Input value={form.phone} onChange={(e) => up("phone", e.target.value)} placeholder="+33 6 12 34 56 78" className="rounded-2xl mt-1 h-11" data-testid="form-phone" />
        </div>
      </div>
      <div>
        <Label className="text-xs font-semibold">Email</Label>
        <Input value={form.email} onChange={(e) => up("email", e.target.value)} placeholder="j.dupont@acme.fr" className="rounded-2xl mt-1 h-11" data-testid="form-email" />
      </div>
      <div>
        <div className="flex items-center justify-between">
          <Label className="text-xs font-semibold">Adresse (rue + n°)</Label>
          <button
            type="button"
            onClick={autoDetectFromAddress}
            className="text-[11px] font-semibold text-emerald-400 active:opacity-60"
            data-testid="form-detect-address"
          >
            Détecter
          </button>
        </div>
        <Input value={form.address} onChange={(e) => up("address", e.target.value)} placeholder="12 rue de Paris" className="rounded-2xl mt-1 h-11" data-testid="form-address" />
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div className="col-span-1">
          <Label className="text-xs font-semibold">Code postal</Label>
          <Input
            value={form.postal_code || ""}
            onChange={(e) => up("postal_code", e.target.value.replace(/\D/g, "").slice(0, 5))}
            placeholder="75001"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={5}
            className="rounded-2xl mt-1 h-11"
            data-testid="form-postal-code"
          />
        </div>
        <div className="col-span-2">
          <Label className="text-xs font-semibold">Ville</Label>
          <Input
            value={form.city || ""}
            onChange={(e) => up("city", e.target.value)}
            placeholder="Paris"
            className="rounded-2xl mt-1 h-11"
            data-testid="form-city"
          />
        </div>
      </div>
    </div>
  );
}
